import type { ReactNode } from 'react'
import { normalizarDirecao } from '../../dominio/tabletop/direcao'
import type { ObjetoTabletop } from '../../dominio/tabletop/tipos'
import {
  IconeConstrucao,
  IconeDestravado,
  IconeDuplicar,
  IconeCalco,
  IconeEstadoClinico,
  IconeGirar,
  IconeOculto,
  IconeRemover,
  IconeTravado,
  IconeVisivel,
} from '../icones'

interface PropsBase {
  readonly objeto: ObjetoTabletop
  /** só para veículo sobre rodas: selecionar não balança mais o carro, o teste é este botão */
  readonly onTestarEstabilidade?: () => void
  /** ações operacionais do objeto na sessão (ex.: riscos: varrer, isolar, acionar concessionária) */
  readonly conteudoOperacional?: ReactNode
  /** só aparece para vítimas: atalho direto pra ficha/condição clínica na cena */
  readonly onAbrirFichaClinica?: () => void
  readonly fichaClinicaAtiva?: boolean
}

/** Montar: todas as ações de edição da peça. */
interface PropsEdicao extends PropsBase {
  readonly somenteOperacional?: false
  readonly onGirar90: () => void
  readonly onGirar?: (delta: number) => void
  readonly onDefinirDirecao?: (graus: number) => void
  readonly onIniciarDirecao?: () => void
  readonly onFinalizarDirecao?: () => void
  readonly onOlharPara?: () => void
  readonly bloqueioCenario?: boolean
  readonly onDuplicar: () => void
  readonly onAlternarBloqueio: () => void
  readonly onAlternarOculto: () => void
  readonly onRemover: () => void
  readonly onAbrirEditor: () => void
}

/**
 * Atender: a cena está travada, então a barra mostra só as ações
 * operacionais (testar estabilidade), sem girar, duplicar, ocultar, remover
 * ou editar.
 */
interface PropsOperacional extends PropsBase {
  readonly somenteOperacional: true
}

type Props = PropsEdicao | PropsOperacional

/**
 * Cartão flutuante sobre a cena 3D com as ações mais comuns da peça
 * selecionada — girar 90°, duplicar, travar, ocultar, remover e abrir o
 * editor completo (posição final/dano/cor para veículo; perfil/postura/
 * vínculo para vítima). Existe pra não obrigar quem está montando a cena a
 * abrir a aba Objetos só pra travar ou ocultar uma peça (Parte B5).
 */
export function BarraAcaoRapidaSelecao(p: Props) {
  const bloqueado = p.objeto.bloqueado === true
  const oculto = p.objeto.oculto === true
  const direcao = Math.round(normalizarDirecao(p.objeto.transformacao.rotacao[1] * 180 / Math.PI))
  const podeVirar = !bloqueado && !(p.objeto.vitima?.local.tipo === 'veiculo') &&
    (p.somenteOperacional || !p.bloqueioCenario || Boolean(p.objeto.equipamento))

  return (
    <div
      className={`tt-barra-selecao ${p.conteudoOperacional ? 'tt-barra-selecao--cartao' : ''}`}
      role="toolbar" aria-label={`Ações rápidas de ${p.objeto.nome}`}>
      {p.conteudoOperacional ?? <span className="tt-barra-selecao__nome">{p.objeto.nome}</span>}

      {p.onTestarEstabilidade && (
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario"
          onClick={p.onTestarEstabilidade}
          title="Empurrar o veículo para ver se ainda balança"
        >
          <IconeCalco />
          Testar estabilidade
        </button>
      )}
      {p.objeto.tipo === 'vitima' && p.onAbrirFichaClinica && (
        <button
          type="button"
          className={`btn-acao-cad ${p.fichaClinicaAtiva ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
          onClick={p.onAbrirFichaClinica}
          title={p.fichaClinicaAtiva ? 'Ocultar condição clínica na cena' : 'Ver condição clínica da vítima (sinais vitais, XABCDE)'}
          aria-pressed={p.fichaClinicaAtiva}
        >
          <IconeEstadoClinico />
          Condição Clínica
        </button>
      )}
      {!p.somenteOperacional && (
        <>
          {p.onGirar && p.onDefinirDirecao ? <fieldset className="tt-direcao-simples" disabled={!podeVirar}>
            <legend>Direção{!podeVirar ? ' · Bloqueada' : ''}</legend>
            <button type="button" onClick={() => p.onGirar?.(-15)} aria-label="Girar 15° para a esquerda"><IconeGirar /> -15°</button>
            <label>
              <span>{direcao}°</span>
              <input type="range" aria-label="Direção do elemento" min={0} max={360} step={1} value={direcao}
                onPointerDown={e => { e.currentTarget.setPointerCapture?.(e.pointerId); p.onIniciarDirecao?.() }}
                onPointerUp={() => p.onFinalizarDirecao?.()}
                onPointerCancel={() => p.onFinalizarDirecao?.()}
                onKeyDown={e => { if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) p.onIniciarDirecao?.() }}
                onKeyUp={() => p.onFinalizarDirecao?.()}
                onBlur={() => p.onFinalizarDirecao?.()}
                onChange={e => p.onDefinirDirecao?.(Number(e.target.value))} />
            </label>
            <button type="button" onClick={() => p.onGirar?.(15)} aria-label="Girar 15° para a direita"><IconeGirar /> +15°</button>
            {p.onOlharPara && ['bombeiro', 'bombeiroMedico'].includes(p.objeto.tipo) &&
              <button type="button" onClick={p.onOlharPara}>Olhar para…</button>}
          </fieldset> : <button type="button" disabled={!podeVirar} className="btn-acao-cad btn-acao-cad--secundario" onClick={p.onGirar90} title="Girar 90°">
            <IconeGirar />90°
          </button>}
          <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={p.onDuplicar} title="Duplicar">
            <IconeDuplicar />
          </button>
          <button
            type="button"
            className={`btn-acao-cad ${bloqueado ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.onAlternarBloqueio}
            title={bloqueado ? 'Destravar' : 'Travar no lugar'}
          >
            {bloqueado ? <IconeTravado /> : <IconeDestravado />}
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={p.onAlternarOculto}
            title={oculto ? 'Mostrar' : 'Ocultar'}
          >
            {oculto ? <IconeOculto /> : <IconeVisivel />}
          </button>
          <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={p.onAbrirEditor} title="Editor completo">
            <IconeConstrucao />
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--perigo"
            onClick={p.onRemover}
            title="Remover"
          >
            <IconeRemover />
          </button>
        </>
      )}
    </div>
  )
}

